import localStore from 'store';

import { useMultiClusterStore } from '@/stores/multiCluster';

import constants from '../constants';
import type { ApiConfig, FullRequestParams } from './api';
import { HttpClient } from './api';

export const PROXY_SERVICE_PREFIX = '/dashboardbff-svc/v0/remote/';

type BaseURLExtension = {
  // The prefix of url that is constant for all requests. Usually it is /k10
  baseURL?: string;
  // Service base url is a fragment of url prefix (part of baseURL) that is specific to the service.
  // This is separated to fulfill MC proxy logic
  serviceBaseURL?: string;
};

export type CustomRequestParams = FullRequestParams &
  BaseURLExtension & {
    clusterId?: string;
    // Set it to true  when request should not be proxied.
    shouldNotBeProxied?: boolean;
  };

type HTTPClientApiConfig<T> = ApiConfig<T> & BaseURLExtension;

export const getProxyClusterId = (userSpecifiedClusterId?: string, shouldNotBeProxied = false) => {
  const multiClusterStore = useMultiClusterStore();
  const currentClusterName = multiClusterStore.proxiedCluster;
  const clusterId = userSpecifiedClusterId ?? currentClusterName ?? undefined;

  // If non-primary cluster is specifically asked or currently in scope - return cluster id to proxy to
  if (!shouldNotBeProxied && clusterId !== undefined && !shouldNotProxy(clusterId)) {
    return clusterId;
  }
};

export class MultiClusterHttpClient<SecurityDataType = unknown> extends HttpClient<
  CustomRequestParams,
  SecurityDataType
> {
  private baseURL: string;
  private serviceBaseURL: string;

  constructor({ serviceBaseURL, ...apiConfig }: HTTPClientApiConfig<SecurityDataType>) {
    super(apiConfig);
    this.baseURL = apiConfig.baseURL ?? '';
    this.serviceBaseURL = serviceBaseURL ?? '';
  }

  public request<T = any, _E = any>({
    baseURL: baseURLArgument,
    serviceBaseURL: serviceBaseURLArgument,
    clusterId: clusterIdArgument,
    shouldNotBeProxied = false,
    ...params
  }: CustomRequestParams) {
    const baseURL = (() => {
      const serviceBaseURL = serviceBaseURLArgument ?? this.serviceBaseURL;
      const baseURL = baseURLArgument ?? this.baseURL;

      const clusterIdToProxyTo = getProxyClusterId(clusterIdArgument, shouldNotBeProxied);

      // If non-primary cluster is specifically asked or currently in scope - proxy request to it
      if (clusterIdToProxyTo !== undefined) {
        return baseURL + PROXY_SERVICE_PREFIX + clusterIdToProxyTo + serviceBaseURL;
      }

      return baseURL + serviceBaseURL;
    })();

    return super.request<T>({ ...params, baseURL });
  }
}

// proxying the primary cluster causes 405 errors
function shouldNotProxy(name: string) {
  const multiClusterStore = useMultiClusterStore();
  // the dummy cluster name is used only if user is on a primary
  // but does not have cluster list permissions
  if (name === constants.MULTICLUSTER_DUMMY_PRIMARY_NAME) return true;
  // if no cluster list has been saved to the store yet look for cached name
  if (multiClusterStore.clusters === null) {
    // name of primary is saved to localStorage in fetchClusters
    const cachedPrimaryName = localStore.get(constants.localStorage.PRIMARY);
    // If there is no saved cluster name don't proxy
    if (!cachedPrimaryName) return true;
    return name === cachedPrimaryName;
  }
  return name === multiClusterStore.primaryCluster?.metadata?.name;
}
